/**
 * Reconcile server-qualified gifts, one cart operation at a time.
 * A shopper's decline belongs to this cart and campaign, not its current total.
 * A gift is confirmed only when Shopify's allocated payable amount is zero.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';

const INIT_FLAG = '__mmFreeGiftUpdaterInitialized';
const STORAGE_KEY = 'mm-gift-preferences-v2';
const state = {
  pending: null, queued: false, scope: '', declines: new Set(),
  pendingDeclines: new Set(), attempts: new Set(), messages: new Map(),
  manual: new Map(), checkoutPending: false, allowCheckout: false,
  lastAnnouncement: '',
};
const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const isGift = (item) => Boolean(item?.properties?._gift);

function sectionIds() {
  return [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId))].slice(0, 5);
}

async function request(url, config = {}, responseType = 'json') {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { credentials: 'same-origin', ...config, signal: controller.signal });
    const data = responseType === 'text' ? await response.text() : await response.json();
    if (!response.ok || data?.status >= 400) throw new Error(data?.description || 'Cart request failed');
    return data;
  } finally {
    window.clearTimeout(timeout);
  }
}

function route(path) {
  return `${window.Shopify?.routes?.root || '/'}${path}`;
}

function payable(item) {
  if (Array.isArray(item.discount_allocations)) {
    return Math.max(0, Number(item.original_line_price) - item.discount_allocations.reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0));
  }
  return Number(item.final_line_price ?? item.line_price ?? Infinity);
}

function scopeHash(value) {
  let hash = 2166136261;
  for (const char of value) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(36);
}

function readPreferences() {
  try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}'); } catch (_) { return {}; }
}

function persistDeclines() {
  try {
    const preferences = readPreferences();
    preferences[state.scope] = { declined: [...state.declines], updated: Date.now() };
    const recent = Object.entries(preferences).sort((a, b) => b[1].updated - a[1].updated).slice(0, 12);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(recent)));
  } catch (_) { /* Storage restrictions must not break shopping; memory still retains the choice. */ }
}

function useCartScope(cart, campaign) {
  const nextScope = scopeHash(`${String(cart.token || 'current').split('?')[0]}:${campaign}`);
  if (state.scope !== nextScope) {
    state.scope = nextScope;
    state.declines = new Set(readPreferences()[nextScope]?.declined || []);
    state.attempts.clear();
    state.messages.clear();
  }
  if (state.pendingDeclines.size) {
    for (const id of state.pendingDeclines) state.declines.add(id);
    state.pendingDeclines.clear();
    persistDeclines();
  }
}

function parseGiftConfig(root) {
  const marker = root.querySelector('[data-gift-qualifying-ids]');
  return {
    campaign: marker?.dataset.giftCampaign || 'inactive',
    qualified: new Set((marker?.dataset.giftQualifyingIds || '').split(',').filter(Boolean)),
    candidates: Array.from(root.querySelectorAll('[data-cart-gift][data-auto-add="true"]'), (container) => {
      const button = container.querySelector('.cart-gift__add-btn');
      return { id: container.dataset.productId, variantId: Number(button?.dataset.variantId), name: button?.dataset.productName || 'gift', enabled: !button?.disabled };
    }),
  };
}

async function snapshot() {
  const cart = await request(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
  const ids = sectionIds();
  if (!ids.length) return { cart, ...parseGiftConfig(document) };
  // Eligibility comes from freshly rendered Liquid, including collection exclusions,
  // currency and campaign dates. Never add from an out-of-date drawer snapshot.
  // Use the same single-section endpoint as the theme's section renderer.
  // Bundled GET sections may be null for a section-group header in draft preview.
  const sectionEntries = await Promise.all(ids.map(async (id) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', id);
    const html = await request(url.toString(), { headers: { Accept: 'text/html' }, cache: 'no-store' }, 'text');
    return [id, html];
  }));
  const sections = Object.fromEntries(sectionEntries);
  const html = Object.values(sections).filter((value) => typeof value === 'string').join('\n');
  if (!html) throw new Error('Could not verify gift eligibility');
  const root = new DOMParser().parseFromString(html, 'text/html');
  return { cart, sections, ...parseGiftConfig(root) };
}

function signature(cart) {
  return JSON.stringify({
    items: cart.items.filter((item) => !isGift(item)).map((item) => [item.variant_id, item.quantity, item.original_line_price, payable(item)]),
    codes: cart.discount_codes || [],
    discounts: (cart.cart_level_discount_applications || []).filter((item) => item.type === 'discount_code').map((item) => item.title),
  });
}

function announceCart(cart) {
  // A settings/stock change can alter the drawer even when no line item changes.
  // Publish that fresh HTML once, without creating recurring self-update events.
  const announcement = JSON.stringify(cart);
  if (announcement === state.lastAnnouncement) return;
  state.lastAnnouncement = announcement;
  document.dispatchEvent(new CartUpdateEvent(cart, 'free-gift-updater', {
    source: 'free-gift-updater', itemCount: cart.item_count, sections: cart.sections,
  }));
}

async function removeGift(item) {
  const body = { id: item.key, quantity: 0, sections: sectionIds(), sections_url: window.location.pathname };
  try {
    const result = await request(route('cart/change.js'), {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
    });
    announceCart(result);
  } catch (error) {
    state.messages.set(String(item.product_id), 'We couldn’t remove this gift. Please use Remove in your cart or try again before checkout.');
    throw error;
  }
}

async function addGift(candidate) {
  const body = new FormData();
  body.append('id', String(candidate.variantId));
  body.append('quantity', '1');
  body.append('properties[_gift]', 'true');
  body.append('sections', sectionIds().join(','));
  body.append('sections_url', window.location.pathname);
  const config = fetchConfig('javascript', { body });
  await request(window.Theme?.routes?.cart_add_url || route('cart/add.js'), {
    ...config, headers: { ...config.headers, Accept: 'application/json' },
  });
  // Do not render an unverified gift as free. Refresh after checking its allocation.
}

function showFeedback() {
  document.querySelectorAll('[data-cart-gift]').forEach((container) => {
    const id = container.dataset.productId;
    const button = container.querySelector('.cart-gift__add-btn');
    const status = container.querySelector('.gift-ladder__status');
    if (state.declines.has(id) && button && !button.disabled) {
      button.textContent = 'Add gift back';
      if (status) status.textContent = 'Gift removed — you can add it back.';
    }
    let message = container.querySelector('.cart-gift__error');
    if (state.messages.has(id)) {
      if (!message) {
        message = document.createElement('p');
        message.className = 'cart-gift__error';
        message.setAttribute('role', 'status');
        (container.querySelector('.gift-ladder__info') || container).append(message);
      }
      message.textContent = state.messages.get(id);
      if (status) status.textContent = 'Gift not added.';
    } else { message?.remove(); }
    if (button?.getAttribute('aria-busy') === 'true') {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
}

async function waitForCart() {
  const deadline = Date.now() + 8000;
  while (document.querySelector('cart-items-component.cart-items-disabled')) {
    if (Date.now() > deadline) throw new Error('Cart update is still pending');
    await pause(80);
  }
}

async function reconcilePass() {
  await waitForCart();
  let current = await snapshot();
  useCartScope(current.cart, current.campaign);
  // Remove a stale or charged reward before another reward is attempted. Only
  // _gift lines are owned here; matching products bought normally are untouched.
  let removed = false;
  for (const item of current.cart.items.filter(isGift)) {
    const id = String(item.product_id);
    const charged = payable(item) !== 0;
    if (item.properties?._mm_choice_gift || charged || !current.qualified.has(id) || state.declines.has(id)) {
      if (!current.qualified.has(id)) {
        for (const attempt of state.attempts) if (attempt.startsWith(`${id}:`)) state.attempts.delete(attempt);
        state.messages.delete(id);
      }
      if (charged && current.qualified.has(id)) {
        state.messages.set(id, 'This gift could not be made free with your current offers. It was removed so you won’t be charged. You can retry after changing your discount.');
        state.attempts.add(`${id}:${signature(current.cart)}`);
      }
      await removeGift(item);
      removed = true;
    }
  }
  if (removed) current = await snapshot();
  useCartScope(current.cart, current.campaign);
  const manual = new Map(state.manual);
  state.manual.clear();
  for (const id of manual.keys()) { state.declines.delete(id); state.pendingDeclines.delete(id); }
  if (manual.size) persistDeclines();
  const candidates = new Map(current.candidates.map((candidate) => [candidate.id, candidate]));
  for (const [id, candidate] of manual) candidates.set(id, candidate);
  for (const candidate of candidates.values()) {
    const id = candidate.id;
    if (!current.qualified.has(id) || !candidate.enabled || !Number.isSafeInteger(candidate.variantId) || candidate.variantId <= 0) continue;
    if (current.cart.items.some((item) => isGift(item) && String(item.product_id) === id)) continue;
    if (state.declines.has(id) || state.pendingDeclines.has(id)) continue;
    const attempt = `${id}:${signature(current.cart)}`;
    if (!manual.has(id) && state.attempts.has(attempt)) continue;
    state.attempts.add(attempt);
    state.messages.delete(id);
    try {
      await addGift(candidate);
      current = await snapshot();
      const added = current.cart.items.filter((item) => isGift(item) && String(item.product_id) === id);
      if (!added.length) throw new Error('Gift was not added');
      let cleanedUp = false;
      for (const item of added) {
        if (payable(item) !== 0 || !current.qualified.has(id) || state.declines.has(id) || state.pendingDeclines.has(id)) {
          if (payable(item) !== 0) state.messages.set(id, 'This gift could not be made free with your current offers. It was removed so you won’t be charged. You can retry after changing your discount.');
          await removeGift(item);
          cleanedUp = true;
        }
      }
      if (cleanedUp) current = await snapshot();
      announceCart({ ...current.cart, sections: current.sections });
    } catch (_) {
      if (!state.messages.has(id)) state.messages.set(id, 'We couldn’t add your gift automatically. Please try again using the button.');
      // An interrupted POST can still have reached Shopify. Re-fetch so the next
      // verification (including checkout) can remove any charged line safely.
      current = await snapshot();
      let cleanedUp = false;
      for (const item of current.cart.items.filter((item) => isGift(item) && String(item.product_id) === id)) {
        if (payable(item) !== 0 || !current.qualified.has(id) || state.declines.has(id) || state.pendingDeclines.has(id)) {
          await removeGift(item);
          cleanedUp = true;
        } else { state.messages.delete(id); }
      }
      if (cleanedUp) current = await snapshot();
      announceCart({ ...current.cart, sections: current.sections });
    }
  }
  announceCart({ ...current.cart, sections: current.sections });
  showFeedback();
  if (current.cart.items.some((item) => isGift(item) && payable(item) !== 0)) throw new Error('Gift price could not be verified');
  return current.cart;
}

async function reconcile() {
  if (state.pending) { state.queued = true; return state.pending; }
  state.pending = (async () => {
    let cart;
    do { state.queued = false; cart = await reconcilePass(); } while (state.queued);
    return cart;
  })();
  try { return await state.pending; } finally { state.pending = null; showFeedback(); }
}

function scheduleReconcile(event) {
  if (event?.detail?.data?.source === 'free-gift-updater') return;
  // Another cart renderer may have replaced the HTML since our last snapshot.
  if (event?.type === ThemeEvents.cartUpdate || event?.type === ThemeEvents.discountUpdate) state.lastAnnouncement = '';
  window.clearTimeout(scheduleReconcile.timer);
  scheduleReconcile.timer = window.setTimeout(() => reconcile().catch((error) => { console.warn('[MM gifts]', error.message); }), 160);
}

function handleRemoval(event) {
  const remove = event.target.closest?.('.cart-items__remove');
  const row = remove?.closest('[data-is-gift="true"]');
  if (!row || remove.disabled) return;
  const id = row.dataset.productId;
  state.pendingDeclines.add(id);
  state.declines.add(id);
  state.manual.delete(id);
  state.messages.delete(id);
  if (state.scope) persistDeclines();
}

function handleGiftClick(event) {
  const swatch = event.target.closest?.('.cart-gift__swatch');
  if (swatch && !swatch.disabled) {
    const container = swatch.closest('[data-cart-gift]');
    container?.querySelectorAll('.cart-gift__swatch').forEach((item) => item.classList.toggle('cart-gift__swatch--active', item === swatch));
    const button = container?.querySelector('.cart-gift__add-btn');
    if (button) button.dataset.variantId = swatch.dataset.variantId;
  }
  const button = event.target.closest?.('.cart-gift__add-btn');
  if (!button || button.disabled) return;
  const id = button.closest('[data-cart-gift]')?.dataset.productId;
  if (!id) return;
  state.declines.delete(id);
  state.pendingDeclines.delete(id);
  persistDeclines();
  state.manual.set(id, { id, variantId: Number(button.dataset.variantId), name: button.dataset.productName, enabled: true });
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  reconcile().catch((error) => { console.warn('[MM gifts]', error.message); state.messages.set(id, 'We couldn’t update your gift. Please try again.'); showFeedback(); });
}

function checkoutError(message) {
  document.querySelectorAll('.cart__ctas').forEach((container) => {
    let feedback = container.querySelector('.cart-gift__checkout-error');
    if (!feedback) {
      feedback = document.createElement('p');
      feedback.className = 'cart-gift__checkout-error';
      feedback.setAttribute('role', 'alert');
      container.prepend(feedback);
    }
    feedback.textContent = message;
  });
}

async function prepareCheckout(button, form) {
  if (state.checkoutPending) return;
  state.checkoutPending = true;
  button?.setAttribute('aria-busy', 'true');
  try {
    await reconcile();
    // Verify again after all queued changes; never send a charged gift to checkout.
    const cart = await request(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (cart.items.some((item) => isGift(item) && payable(item) !== 0)) throw new Error('Gift is not free');
    const currentForm = document.getElementById(form?.id || 'cart-form') || form;
    const submitter = document.querySelector('button[name="checkout"]');
    state.allowCheckout = true;
    try {
      if (currentForm && submitter) currentForm.requestSubmit(submitter);
      else window.location.assign(route('checkout'));
    } finally { state.allowCheckout = false; }
  } catch (_) {
    checkoutError('We couldn’t finish checking your cart. Please try Checkout again. You can also remove the gift and retry.');
  } finally {
    state.checkoutPending = false;
    button?.removeAttribute('aria-busy');
    document.querySelectorAll('[name="checkout"]').forEach((el) => el.removeAttribute('aria-busy'));
    showFeedback();
  }
}

function interceptCheckout(event) {
  if (state.allowCheckout) return;
  const button = event.type === 'submit' ? event.submitter : event.target.closest?.('[name="checkout"], a[href$="/checkout"]');
  const form = event.type === 'submit' ? event.target : button?.form;
  if (event.type === 'submit') {
    if (button ? button.name !== 'checkout' : form?.id !== 'cart-form') return;
  } else if (!button) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  prepareCheckout(button, form);
}

export function initFreeGiftUpdater() {
  if (window[INIT_FLAG]) return;
  window[INIT_FLAG] = true;
  document.addEventListener('click', handleRemoval, true);
  document.addEventListener('click', interceptCheckout, true);
  document.addEventListener('submit', interceptCheckout, true);
  document.addEventListener('click', handleGiftClick);
  document.addEventListener(ThemeEvents.cartUpdate, scheduleReconcile);
  document.addEventListener(ThemeEvents.discountUpdate, scheduleReconcile);
  // DialogOpenEvent does not bubble. Capture it without refreshing for unrelated dialogs.
  document.addEventListener(DialogOpenEvent.eventName, (event) => {
    if (event.target.matches?.('cart-drawer-component')) scheduleReconcile();
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleReconcile();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleReconcile();
  });
  scheduleReconcile();
}

initFreeGiftUpdater();
